import { useState, useCallback, useEffect } from "react";
import { ITextFieldSubmission } from "@Interfaces";
import { getTextFieldResponses } from "@Services/API/TextFieldResponses";

interface UseTextFieldResponsesReturn {
    isExpanded: boolean;
    responses: ITextFieldSubmission[];
    isLoading: boolean;
    error: string | null;
    toggleExpanded: () => void;
}

/**
 * useTextFieldResponses
 *
 * Custom hook for managing text field responses display state and data fetching.
 * Handles:
 * - Toggle expanded/collapsed state
 * - Fetch responses when expanded
 * - Loading and error states
 * - Cleanup on unmount to prevent memory leaks
 *
 * @param textFieldId - The ID of the text field
 * @returns Object with state and handlers
 */
export const useTextFieldResponses = (
    textFieldId: string
): UseTextFieldResponsesReturn => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [responses, setResponses] = useState<ITextFieldSubmission[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch responses when component expands
    useEffect(() => {
        if (!isExpanded) {
            return;
        }

        let isMounted = true;

        const fetchResponses = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const result = await getTextFieldResponses(textFieldId);
                // Only update state if component is still mounted
                if (isMounted) {
                    setResponses(result.data);
                }
            } catch (err) {
                if (isMounted) {
                    console.error("Error fetching text field responses:", err);
                    setError(
                        err instanceof Error
                            ? err.message
                            : "Failed to load responses"
                    );
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchResponses();

        // Cleanup function to prevent state updates on unmounted component
        return () => {
            isMounted = false;
        };
    }, [isExpanded, textFieldId]);

    const toggleExpanded = useCallback(() => {
        setIsExpanded((prev) => !prev);
    }, []);

    return {
        isExpanded,
        responses,
        isLoading,
        error,
        toggleExpanded,
    };
};
